/* eslint-disable consistent-return */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable max-lines-per-function */
import { notify } from "../../feedback/SnackbarProvider";
import { captureException } from "@sentry/react";

import { cloneDeep, get, isEmpty, isEqual } from "lodash";
import omitDeep from "omit-deep-lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FormProvider,
  useForm,
  useFormContext,
  UseFormSetValue,
  FieldValues,
} from "react-hook-form";
import { useResizeDetector } from "react-resize-detector";
import { useDebouncedCallback } from "use-debounce";
import { useCurrentUser } from "../../utils/hooks";
import {
  useTemplateStoreFromContext,
  useIsPersistingField,
} from "./Context/hooks";
import { KEYS_TO_OMMIT_ON_INPUT } from "./ImportFields/helpers";
import PublishTemplateHeading from "./PublishTemplateHeading";
import { hasNewerServerValue } from "./Template.helpers";
import { Field, FieldInput } from "./types";
import { useShallow } from "zustand/react/shallow";
import * as types from "_graphql-types-frontend/graphql";
import uuid from "../../utils/uuid";

const isChanged = ({
  formDataValues,
  fieldData,
  entityKey,
  fieldKey,
}: {
  formDataValues: { [key: string]: { [key: string]: Field } };
  fieldData: Field;
  entityKey: string;
  fieldKey: string;
}) => {
  const currentValue = get(formDataValues, [entityKey, fieldKey]);
  if (!currentValue) return true;
  if (!fieldData) return true;

  if (
    isEqual(currentValue.value ?? null, fieldData.value ?? null) &&
    isEqual(
      currentValue.notApplicable ?? null,
      fieldData.notApplicable ?? null
    ) &&
    isEqual(currentValue.documentIds ?? null, fieldData.documentIds ?? null)
  ) {
    return false;
  }

  return true;
};

const useAutoSave = (serverDataValues: {
  [key: string]: { [key: string]: Field };
}) => {
  const currentUser = useCurrentUser();
  const { saveFields } = useTemplateStoreFromContext()(
    useShallow(state => ({
      saveFields: state.saveFields,
    }))
  );

  const [upForRetry, changeUpForRetry] = useState<
    Record<string, Record<string, FieldInput>>
  >({});

  const { subscribe, reset, trigger } = useFormContext();

  const autoSave = useCallback(
    async (changedValues: Record<string, Record<string, FieldInput>>) => {
      const inputData = {
        ...upForRetry,
        ...changedValues,
      };

      if (!isEmpty(inputData)) {
        try {
          await saveFields(inputData, currentUser.writeDraftState);
          changeUpForRetry({});
          reset(inputData, {
            keepValues: true,
            keepDirty: false,
            keepTouched: true,
            keepIsValid: true,
            keepErrors: true,
          });
        } catch (e: any) {
          notify({ severity: "error",
            key: "FieldAutoSaveError",
            message: `Error Auto Saving Drafts: ${e.message}`,
          });
          changeUpForRetry(inputData);
          throw e;
        }
      }
    },
    [upForRetry, saveFields, currentUser.writeDraftState]
  );

  const debouncedSubmit = useDebouncedCallback(
    async ({ values, dirtyFields }: { values: any; dirtyFields: any }) => {
      try {
        const changedValues: Record<string, Record<string, FieldInput>> = {};
        const fieldsToTrigger: string[] = [];

        // Only process dirty fields
        for (const [entityKey, entityDirtyFields] of Object.entries(
          dirtyFields || {}
        )) {
          if (!entityDirtyFields || typeof entityDirtyFields !== "object")
            continue;

          for (const [fieldKey, isDirty] of Object.entries(
            entityDirtyFields as any
          )) {
            if (!isDirty) continue;

            const currentValue = values[entityKey]?.[fieldKey];

            fieldsToTrigger.push(`${entityKey}.${fieldKey}`);
            changedValues[entityKey] = changedValues[entityKey] || {};
            changedValues[entityKey][fieldKey] = omitDeep(
              currentValue,
              KEYS_TO_OMMIT_ON_INPUT
            );
          }
        }

        if (fieldsToTrigger.length > 0) {
          const isValid = await trigger(fieldsToTrigger);
          if (isValid) {
            await autoSave(changedValues);
          }
        }
      } catch (error) {
        // autoSave already surfaced a user-facing notification.error and re-threw;
        // capture here (fire-and-forget debounced callback) so the failure is not
        // lost, without producing an unhandled rejection.
        captureException(error, { tags: { feature: "templateForm:autoSave" } });
      }
    },
    10000
  );

  useEffect(() => {
    const callback = subscribe({
      formState: { values: true, dirtyFields: true },
      callback: ({ values, dirtyFields }) => {
        debouncedSubmit({ values, dirtyFields });
      },
    });
    return () => callback();
  }, [subscribe, debouncedSubmit]);

  return { autoSave: debouncedSubmit };
};

/**
 * Write a server-originated field change into the form so the rendered input
 * actually picks it up.
 *
 * `setValue` on the parent path alone is not enough. The diligence inputs
 * register the LEAF (`investment.<fieldKey>.value`, via
 * useTemplateController/useController), but the parent
 * `investment.<fieldKey>` is registered too — it has its own `_f`. RHF's
 * setValue checks exactly that: a path with `_f` is treated as a leaf, so it
 * writes `_formValues` and notifies subscribers of the PARENT name only. The
 * control subscribed to `...value` is never told to re-read, so the new value
 * sits in form state while the screen keeps showing the old answer until a
 * reload — which is what made an agent-written draft look like it had not
 * happened.
 *
 * Setting each key of the change explicitly notifies whichever of them the
 * inputs registered.
 */
function setServerFieldValue(
  setValue: UseFormSetValue<FieldValues>,
  path: string,
  change: NonNullable<Field>
): void {
  setValue(path, change);
  for (const [key, value] of Object.entries(change)) {
    // Skip undefined: the parent setValue above already wrote the whole object
    // into form state, and pushing undefined at a registered leaf flips its
    // input from controlled to uncontrolled.
    if (value === undefined) continue;
    setValue(`${path}.${key}`, value);
  }
}

const useUpdateFormWithServerUpdates = (serverDataValues: {
  [key: string]: { [key: string]: Field };
}) => {
  const {
    getValues,
    setValue,
    formState: { defaultValues },
  } = useFormContext();
  const isPersistingField = useIsPersistingField();

  const [formServerUpdates, setFormServerUpdates] = useState<
    { new: NonNullable<Field>; prev: NonNullable<Field> }[]
  >([]);

  // Use useMemo instead of useEffect
  const serverUpdates = useMemo(() => {
    if (!defaultValues) return [];

    const currentFormState = getValues();
    return Object.values(serverDataValues) // Use serverDataValues instead of formServerUpdates
      .map(value => Object.values(value))
      .flat()
      .filter((field: Field) => {
        if (!field) return false;
        const currentFormField = get(currentFormState, [
          field.ownerType,
          field.fieldKey,
        ]);
        // Absent from the form entirely = a value that appeared AFTER mount:
        // an agent draft, the Ranger worker, or another user. The form was
        // seeded from whatever existed at mount, so there is no prior value to
        // compare against — take it. Requiring currentFormField here is what
        // made an agent-written draft never show up without a page reload.
        //
        // Anything else goes through hasNewerServerValue, which is what keeps
        // this from throwing: a field registered only by subpath materializes
        // as `{ value }` — truthy, but with no modifyDate to dereference
        // (MERCURY-J2) — and the timestamps can arrive as ISO strings.
        if (!currentFormField) return true;
        return hasNewerServerValue(currentFormField, field);
      });
  }, [serverDataValues, defaultValues, getValues]);

  // Separate effect for handling the updates
  useEffect(() => {
    if (serverUpdates.length) {
      const currentFormState = getValues();

      serverUpdates.forEach(change => {
        if (!change) return;
        const currentField = get(currentFormState, [
          change.ownerType,
          change.fieldKey,
        ]);

        // Appeared after mount — seed it straight into the form. Not added to
        // the change log: there is no previous value to show a diff against.
        if (!currentField) {
          setServerFieldValue(
            setValue,
            `${change.ownerType}.${change.fieldKey}`,
            change
          );
          return;
        }

        if (isPersistingField(change)) {
          setValue(
            `${change.ownerType}.${change.fieldKey}.modifyDate`,
            change.modifyDate
          );
        } else {
          if (
            isChanged({
              formDataValues: currentFormState,
              fieldData: change,
              entityKey: change.ownerType,
              fieldKey: change.fieldKey,
            })
          ) {
            setFormServerUpdates(prev =>
              [
                {
                  new: change,
                  prev: currentField,
                },
              ]
                .concat(prev)
                .slice(0, 100)
            );
          }
          setServerFieldValue(
            setValue,
            `${change.ownerType}.${change.fieldKey}`,
            change
          );
        }
      });
    }
  }, [serverUpdates, setValue, isPersistingField]);

  return formServerUpdates;
};

const TemplateServerWatcher = React.memo(function TemplateServerWatcher({
  serverDataValues,
}: {
  serverDataValues: { [key: string]: { [key: string]: Field } };
}) {
  const formServerUpdates = useUpdateFormWithServerUpdates(serverDataValues);
  const { autoSave } = useAutoSave(serverDataValues);

  // The autosave.flush ends up being slow due to the behavior of useController.
  // Causes everything subscribed to useController to update?
  return (
    <PublishTemplateHeading
      formServerUpdates={formServerUpdates}
      onPublishToolBarClick={autoSave.flush}
    />
  );
});

function TemplateFormProvider({
  serverDataValues,
  children,
}: {
  serverDataValues: { [key: string]: { [key: string]: Field } };
  children: React.ReactNode;
}) {
  const defaultValues = useMemo(() => {
    return cloneDeep(serverDataValues);
  }, []);

  const methods = useForm({
    // https://github.com/react-hook-form/react-hook-form/discussions/3715
    defaultValues,
  });

  const { width, ref } = useResizeDetector({
    refreshMode: "debounce",
    refreshRate: 100,
    handleHeight: false,
  });

  const FixedStyle: React.CSSProperties = useMemo(() => {
    const rect = ref.current?.getBoundingClientRect();
    return {
      position: "fixed",
      bottom: 0,
      left: rect?.left || 0,
      width,
      zIndex: 2,
    };
  }, [width, ref.current]);

  return (
    <div className="main-content-template" ref={ref}>
      <FormProvider {...methods}>
        <div style={FixedStyle}>
          <TemplateServerWatcher serverDataValues={serverDataValues} />
        </div>
        <form>{children}</form>
      </FormProvider>
    </div>
  );
}

function ownerTypeFromTemplateKey(templateKey: string): types.FieldOwnerType {
  if (templateKey.toLowerCase().startsWith("investment")) {
    return types.FieldOwnerType.Investment;
  }
  if (templateKey.toLowerCase().startsWith("firm")) {
    return types.FieldOwnerType.Firm;
  }
  if (templateKey.toLowerCase().startsWith("company")) {
    return types.FieldOwnerType.Company;
  }
  throw new Error(`Invalid ownerType from TemplateKey: ${templateKey}`);
}

/**
 * The field keys the edit form must hydrate for one entity: every field that has
 * a published value OR a draft, minus the envelope/owner-id keys.
 *
 * The union matters. publishedData/draftData are fieldKey maps built from the
 * generic `fields.values` array (fieldValuesToMap), which contains only fields
 * that actually have a row — so a field drafted but never published has no
 * published key at all. Keying off the published map alone silently dropped
 * those drafts, and on an entity with nothing published yet it produced an
 * entirely empty form.
 */
export function templateFormFieldKeys(
  publishedValues: Record<string, unknown>,
  draftValues: Record<string, unknown>,
  ownerIdFieldKeys: string[] = []
): string[] {
  const skip = new Set(["id", "__typename", "state", ...ownerIdFieldKeys]);
  return [
    ...new Set([...Object.keys(publishedValues), ...Object.keys(draftValues)]),
  ].filter(fieldKey => !skip.has(fieldKey));
}

function useCalculateFormServerValues() {
  const currentUser = useCurrentUser();
  const {
    publishedData,
    draftData,
    editFieldData,
    entityOwnerIds,
    getModifyDate,
  } = useTemplateStoreFromContext()(
    useShallow(state => ({
      publishedData: state.publishedData,
      draftData: state.draftData,
      editFieldData: state.editFieldData,
      entityOwnerIds: state.entityOwnerIds,
      getModifyDate: state.getModifyDate,
    }))
  );

  const memoizedFormData = useMemo(() => {
    if (
      !publishedData ||
      !draftData ||
      !currentUser.email ||
      !editFieldData?.data
    ) {
      return null;
    }

    const initialTemplateDate = getModifyDate();
    const formData: Record<string, Record<string, Field>> = {};

    for (const [entityKey, publishedValues] of Object.entries(publishedData)) {
      const draftValues = draftData[entityKey] ?? {};
      const ownerId = editFieldData.data[entityKey].id;
      const ownerType = ownerTypeFromTemplateKey(entityKey);

      if (!ownerId) {
        console.warn("No ownerId for templateKey! Empty Form used", entityKey);
        continue;
      }

      const fieldKeys = templateFormFieldKeys(
        publishedValues,
        draftValues,
        entityOwnerIds
          ? Object.values(entityOwnerIds).map(({ fieldKey }) => fieldKey)
          : []
      );

      for (const fieldKey of fieldKeys) {
        const publishValue = publishedValues[fieldKey];
        const draftValue = draftValues[fieldKey];

        formData[entityKey] = formData[entityKey] || {};
        formData[entityKey][fieldKey] = {
          value: undefined,
          createDate: new Date(),
          createUser: currentUser.email as string,
          id: uuid(),
          ...(draftValue ?? publishValue),
          ownerId,
          fieldKey,
          ownerType,
          state: currentUser.writeDraftState,
          modifyDate:
            draftValue?.modifyDate || publishValue?.modifyDate
              ? new Date(draftValue?.modifyDate || publishValue?.modifyDate)
              : initialTemplateDate,
          modifyUser:
            draftValue?.modifyUser ||
            publishValue?.modifyUser ||
            currentUser.email,
          documents: draftValue?.documents || publishValue?.documents || [],
          documentIds:
            draftValue?.documentIds || publishValue?.documentIds || [],
        };
      }
    }
    return formData;
  }, [
    publishedData,
    draftData,
    currentUser.email,
    currentUser.writeDraftState, // Add this
    editFieldData?.data,
    getModifyDate,
    entityOwnerIds,
  ]);

  return memoizedFormData;
}

export default function TemplateForm({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element | null {
  const serverDataValues = useCalculateFormServerValues();

  if (!serverDataValues) {
    // probably loading.
    return null;
  }

  return (
    <TemplateFormProvider serverDataValues={serverDataValues}>
      {children}
    </TemplateFormProvider>
  );
}
